import React from "react";
import { Descriptions, Space, Tag } from "antd";
import { useTranslation } from "react-i18next";

type Props = {
  fields: Record<string, unknown>;
};

function ringsFromFields(fields: Record<string, unknown>): number[][][] {
  const rings = fields.preview_rings;
  if (!Array.isArray(rings)) return [];
  return rings.filter(
    (ring): ring is number[][] =>
      Array.isArray(ring) && ring.every((pt) => Array.isArray(pt) && pt.length >= 2),
  ) as number[][][];
}

/** SVG schematic of plot polygon(s) — no map tiles required for demo. */
export function PolygonSchematic({ fields }: Props) {
  const { t } = useTranslation();
  const rings = ringsFromFields(fields);
  const geometryValid = fields.geometry_valid === true;
  const issues = Array.isArray(fields.geometry_issues)
    ? (fields.geometry_issues as string[])
    : [];

  const width = 280;
  const height = 180;
  const pad = 12;

  let pathD = "";
  if (rings.length > 0) {
    const all = rings.flat();
    const lons = all.map((p) => p[0]);
    const lats = all.map((p) => p[1]);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const dx = maxLon - minLon || 1e-6;
    const dy = maxLat - minLat || 1e-6;
    const sx = (width - 2 * pad) / dx;
    const sy = (height - 2 * pad) / dy;
    const scale = Math.min(sx, sy);
    const ox = (width - dx * scale) / 2;
    const oy = (height - dy * scale) / 2;
    pathD = rings
      .map((ring) => {
        const pts = ring.map(([lon, lat]) => {
          const x = ox + (lon - minLon) * scale;
          const y = height - (oy + (lat - minLat) * scale);
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        });
        return `M ${pts.join(" L ")} Z`;
      })
      .join(" ");
  }

  const areaHa = typeof fields.area_ha === "number" ? fields.area_ha : null;
  const crs = typeof fields.crs === "string" ? fields.crs : "—";
  const polygonCount = typeof fields.polygon_count === "number" ? fields.polygon_count : 0;
  const sourceFormat = typeof fields.source_format === "string" ? fields.source_format : null;

  return (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Space wrap>
        <Tag color={geometryValid ? "success" : "error"}>
          {geometryValid
            ? t("compliance.geometry.valid")
            : t("compliance.geometry.invalid")}
        </Tag>
        {sourceFormat && <Tag>{sourceFormat.toUpperCase()}</Tag>}
        {issues.map((code) => (
          <Tag key={code} color="orange">
            {t(`compliance.geometry.issues.${code}`, { defaultValue: code })}
          </Tag>
        ))}
      </Space>
      <Descriptions size="small" column={2}>
        <Descriptions.Item label={t("compliance.geometry.crs")}>{crs}</Descriptions.Item>
        <Descriptions.Item label={t("compliance.geometry.polygons")}>
          {polygonCount}
        </Descriptions.Item>
        <Descriptions.Item label={t("compliance.geometry.areaHa")}>
          {areaHa != null ? areaHa.toFixed(4) : fields.area != null ? String(fields.area) : "—"}
        </Descriptions.Item>
        <Descriptions.Item label={t("compliance.geometry.geometryType")}>
          {typeof fields.geometry_type === "string" ? fields.geometry_type : "—"}
        </Descriptions.Item>
      </Descriptions>
      {pathD ? (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={t("compliance.geometry.schematicAria")}
          style={{
            background: "var(--ant-color-fill-quaternary, #f5f5f5)",
            borderRadius: 4,
            border: "1px solid #d9d9d9",
          }}
        >
          <path
            d={pathD}
            fill={geometryValid ? "rgba(82, 196, 26, 0.25)" : "rgba(255, 77, 79, 0.25)"}
            stroke={geometryValid ? "#389e0d" : "#cf1322"}
            strokeWidth={2}
          />
        </svg>
      ) : (
        <span>{t("compliance.geometry.noPreview")}</span>
      )}
    </Space>
  );
}
